import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { deactivateUser, listUsers, type User } from '@/features/auth/api'
import { useAuth } from '@/features/auth/store'
import { UserFormDialog } from '@/features/users/UserFormDialog'
import { formatPercent } from '@/lib/money'
import { toast } from '@/lib/toast'

const columns = (canEdit: boolean, onEdit: (user: User) => void): Column<User>[] => [
  {
    key: 'full_name',
    header: 'Name',
    className: 'font-medium',
    sortValue: (user) => user.full_name,
    cell: (user) =>
      canEdit ? (
        <button type="button" onClick={() => onEdit(user)} className="text-left hover:underline">
          {user.full_name}
        </button>
      ) : (
        user.full_name
      ),
  },
  {
    key: 'email',
    header: 'Email',
    className: 'text-slate-500 dark:text-slate-400',
    sortValue: (user) => user.email,
    cell: (user) => user.email,
  },
  {
    key: 'roles',
    header: 'Roles',
    cell: (user) =>
      user.roles.map((role) => role.name).join(', ') || (
        <span className="text-slate-400">None</span>
      ),
  },
  {
    key: 'commission_rate_pct',
    header: 'Commission',
    className: 'tabular-nums',
    sortValue: (user) =>
      user.commission_rate_pct === null ? -1 : Number(user.commission_rate_pct),
    cell: (user) =>
      user.commission_rate_pct === null ? (
        <span className="text-slate-400">Company rate</span>
      ) : (
        formatPercent(user.commission_rate_pct)
      ),
  },
  {
    key: 'is_active',
    header: 'Status',
    sortValue: (user) => (user.is_active ? 0 : 1),
    cell: (user) => (
      <span
        className={
          user.is_active
            ? 'rounded bg-success/10 px-2 py-0.5 text-xs font-medium text-success'
            : 'rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300'
        }
      >
        {user.is_active ? 'Active' : 'Disabled'}
      </span>
    ),
  },
  {
    key: 'last_login_at',
    header: 'Last signed in',
    className: 'text-slate-500 dark:text-slate-400',
    sortValue: (user) => user.last_login_at,
    cell: (user) => (user.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'Never'),
  },
]

export function Users() {
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string[]>([])
  const [dialog, setDialog] = useState<{ user?: User } | null>(null)
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['users', page],
    queryFn: () => listUsers(page),
    // A 403 is a settled answer, not a blip. Retrying it just delays the message.
    retry: false,
  })

  const users = data?.data ?? []
  const meta = data?.meta

  const deactivate = useMutation({
    mutationFn: async (ids: string[]) => {
      // One request per user: the API has no bulk route, and sending them in sequence keeps
      // a partial failure legible -- the ones before it really did go through.
      for (const id of ids) await deactivateUser(id)
      return ids.length
    },
    onSuccess: (count) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      setSelected([])
      toast.success(`${count} account${count === 1 ? '' : 's'} deactivated.`)
    },
    onError: (mutationError: unknown) => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      toast.error(
        mutationError instanceof ApiError
          ? mutationError.message
          : 'Could not reach the server. Nothing further was changed.',
      )
    },
  })

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} account${meta.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        {/* Hidden without the permission -- and refused by the server regardless. */}
        {can('users', 'create') && <Button onClick={() => setDialog({})}>New user</Button>}
      </header>

      <Card>
        <CardHeader
          title="All accounts"
          description="Read from /api/v1/users, guarded by users:read."
        />
        <DataTable
          rows={users}
          columns={columns(can('users', 'update'), (user) => setDialog({ user }))}
          getRowId={(user) => user.id}
          caption="Users"
          isLoading={isLoading}
          error={error}
          emptyMessage="No users found."
          search={{
            placeholder: 'Search name or email…',
            matches: (user, query) =>
              user.full_name.toLowerCase().includes(query) ||
              user.email.toLowerCase().includes(query),
          }}
          selection={
            can('users', 'delete')
              ? {
                  selected,
                  onChange: setSelected,
                  actions: (ids) => (
                    <Button
                      variant="danger"
                      size="sm"
                      loading={deactivate.isPending}
                      onClick={() => deactivate.mutate(ids)}
                    >
                      Deactivate
                    </Button>
                  ),
                }
              : undefined
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: setPage,
                  isFetching,
                  total: meta.total,
                }
              : undefined
          }
        />
      </Card>

      {dialog && (
        // Keyed so switching straight from one user to another rebuilds the form rather
        // than leaving the previous person's values in it.
        <UserFormDialog
          key={dialog.user?.id ?? 'new'}
          open
          user={dialog.user}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  )
}
